'use client';

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { setIsUpdatingCart } from '@slices/cartUpdatingSlice';
import { useAppDispatch } from '@hooks/useAppDispatch';
import type { ProductQuantityControlVariant } from '@molecules/ProductQuantityControl/ProductQuantityControl';
import useSnackBar from '@hooks/useSnackbar';
import { useTranslations } from 'next-intl';
import Config from '@config';
import type { AxfoodCartProductViewModel } from '@occ/api-client';
import useCartActions from '@hooks/useCartActions';
import trackCartModification from '@helpers/analyticsHelpers/trackCartModification';
import useStore from '@hooks/useStore';

export interface AddToCartItem {
  product: AxfoodCartProductViewModel;
  newQuantity: number;
  prevQuantity: number;
  trackingSource: string;
  variant?: ProductQuantityControlVariant;
}

export interface AddProductQueueState {
  cartQueue: {
    [key: string]: AddToCartItem;
  };
}

export type AddToCartCallback = (item: AddToCartItem) => void;

export const DEFAULT_STATE: AddProductQueueState = {
  cartQueue: {},
};

const initialContext = {
  addToCart: () => {
    /**/
  },
};

export const AddProductQueueContext = React.createContext<{
  addToCart: AddToCartCallback;
}>(initialContext);

export interface AddProductQueueProvider {
  children: React.ReactNode;
}

export const AddProductQueueProvider = ({ children }: AddProductQueueProvider) => {
  const [cartQueue, addToCartQueue] = useState(() => DEFAULT_STATE.cartQueue);
  const [localVariant, setLocalVariant] = useState('cart');
  const { postToCart } = useCartActions();
  const clearCartQueue = useCallback(() => addToCartQueue({}), []);
  const postAddProductTimeout = useRef<NodeJS.Timeout>(null);
  const { store } = useStore();
  const dispatch = useAppDispatch();
  const { setSnack } = useSnackBar();
  const tProductCard = useTranslations('productCard');

  const postAddProduct = useCallback(() => {
    postToCart(Object.values(cartQueue), store)
      .then(({ cartCode, wasEmpty, isLoggedIn }) => {
        Object.values(cartQueue).forEach(({ product, newQuantity, prevQuantity, trackingSource }) => {
          trackCartModification(newQuantity, prevQuantity, product, trackingSource, cartCode, wasEmpty, isLoggedIn);
        });
      })
      .catch(() => {
        setSnack({ text: tProductCard('quantityInput.error.message'), icon: null });
      })
      .finally(() => {
        dispatch(setIsUpdatingCart(false));
        clearCartQueue();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartQueue]);

  useEffect(() => {
    if (Object.keys(cartQueue).length > 0) {
      const hasItemToBeRemoved = Object.values(cartQueue).some((item) => item.newQuantity === 0);
      postAddProductTimeout.current = setTimeout(
        postAddProduct,
        hasItemToBeRemoved && localVariant === 'cart'
          ? Config.TIMEOUT.removeProductQueueMs
          : Config.TIMEOUT.addProductQueueMs
      );
    }
    return () => {
      if (postAddProductTimeout.current) clearTimeout(postAddProductTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartQueue]);

  const addToCart = useCallback<AddToCartCallback>(
    ({ product, newQuantity, prevQuantity, trackingSource, variant }) => {
      if (variant) setLocalVariant(variant);
      if (postAddProductTimeout.current) clearTimeout(postAddProductTimeout.current);
      addToCartQueue({
        ...cartQueue,
        [product.code]: {
          product,
          newQuantity,
          prevQuantity,
          trackingSource,
        },
      });
    },
    [cartQueue, addToCartQueue]
  );

  return (
    <AddProductQueueContext.Provider
      value={{
        addToCart,
      }}
    >
      {children}
    </AddProductQueueContext.Provider>
  );
};

const useAddProductQueue = () => useContext(AddProductQueueContext);
export default useAddProductQueue;
